import { z } from 'zod'
import { oc } from '@orpc/contract'

const NUMERIC_CLINICIAN_ID = /^\d+$/
const NOT_NUMERIC = 'Expected a numeric DoseSpot clinician id'

/** The wire value: a numeric clinician id, or `null` to revoke prescribing. */
export const doseSpotClinicianIdSchema = z
  .string()
  .trim()
  .regex(NUMERIC_CLINICIAN_ID, NOT_NUMERIC)
  .nullable()

/**
 * The same rule for the Provider Operations form, where **blank stands for `null`** — one field
 * is both the grant and the revoke, so it has to accept being emptied. Shared with the
 * wire schema so the rule and its message are authored once (`AGENTS.md`).
 */
export const doseSpotClinicianIdFieldSchema = z
  .string()
  .trim()
  .refine((value) => value === '' || NUMERIC_CLINICIAN_ID.test(value), NOT_NUMERIC)

/**
 * What the mapped DoseSpot account is (PD-1592). **Detected by the writer from DoseSpot's
 * roles, never entered on a form** — the form only displays it. A mapping written before
 * the kind existed reads as `prescriber`. Declared here, not in `dosespot/model`, because
 * this file crosses into the browser bundles and only `*.contract.ts` may do that.
 */
export const DOSESPOT_CLINICIAN_KINDS = ['prescriber', 'proxy', 'agent'] as const

export const doseSpotClinicianKindSchema = z.enum(DOSESPOT_CLINICIAN_KINDS)

export type DoseSpotClinicianKindDto = z.infer<typeof doseSpotClinicianKindSchema>

/**
 * Writes (or clears) the DoseSpot clinician id on a Practitioner — the DoseSpot Clinician
 * Mapping. **This is the prescribing grant** (§5.1) — there is no other authorization
 * step, so it validates the id against DoseSpot before saving. Written by a super admin
 * or by a Care Coordinator of the practitioner's Organization (Provider Operations; see
 * `practitioner-ops-access.ts`); the NPI cross-check is the only guard against a
 * mis-mapping.
 *
 * `null` clears the mapping: an immediate revoke, no DoseSpot call. A saved mapping
 * returns the kind DoseSpot's roles resolved to.
 */
export const setPractitionerDoseSpotClinician = oc
  .route({ method: 'POST', path: '/v2/practitioners/{practitionerId}/dosespot-clinician' })
  .input(
    z.object({
      practitionerId: z.string().min(1),
      // Disambiguates a target with roles in more than one Organization; never widens access.
      organizationId: z.string().min(1).optional(),
      clinicianId: doseSpotClinicianIdSchema,
    }),
  )
  .output(
    z.object({
      clinicianId: z.string().nullable(),
      clinicianKind: doseSpotClinicianKindSchema.nullable(),
    }),
  )
